import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import CaseInfoForm from "components/CaseInfoForm";
import CustomPropertyCell from "components/CustomPropertyCell";
import { Case, CaseInfo, CustomCaseProperty, NamedUser } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { fullNameFromParts } from "helpers/name-helpers";
import { useState } from "react";
import { updateCaseClaimantInfo } from "redux/slices/case";
import produce from "immer";
import { customPropertiesFromUI, customPropertiesToUI } from "helpers/customProperties";
import { CaseInfoUI, caseInfoFromUI, caseInfoToUI } from "helpers/caseInfo";
import { isNotUndefined } from "Backend";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { CustomPropertyType } from "generated/graphql";
import { useShowSBR } from "SBR/useShowSBR";

export default function EditCaseInfoDialog({
  canEditCaseInfo,
  open,
  close,
  caseProperties,
  mycase,
  assignees,
  edited,
  group,
}: {
  canEditCaseInfo: boolean;
  open: boolean;
  close: () => void;
  caseProperties: (CustomCaseProperty | undefined)[];
  mycase: Case;
  assignees: NamedUser[];
  edited?: (info: CaseInfo, userAssignee?: NamedUser) => void;
  group?: { name?: string | null; description?: string | null };
}) {
  const caseInfoHandle = useState<CaseInfoUI>(caseInfoToUI(mycase.info));
  const caseInfoValidHandle = useState(true);
  const caseAssigneeHandle = useState<NamedUser | undefined>(() => mycase.userAssignee || undefined);
  const [casePropertyValues, setCasePropertyValues] = useState(
    customPropertiesToUI(caseProperties.filter(isNotUndefined<CustomCaseProperty>), mycase.properties)
  );
  const snackpatch = useAppDispatchWithSnackbar();
  const sbr = useShowSBR();
  const saveFormChanges = () => {
    snackpatch(
      updateCaseClaimantInfo({
        caseId: mycase.id,
        caseInfo: caseInfoFromUI(caseInfoHandle[0]),
        userAssignee: sbr ? undefined : caseAssigneeHandle[0],
        caseProperties: customPropertiesFromUI(casePropertyValues),
      }),
      {
        successMessage: "Case information updated",
        errorMessage: "Case information not updated",
      }
    ).then(({ info, userAssignee }) => {
      if (edited) {
        edited(info, userAssignee);
      }
      close();
    });
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}>
        Edit {fullNameFromParts(mycase.info.claimantGivenName, mycase.info.claimantSurname)}
        {group?.name && ` - ${group?.name}`}
        <DescriptionTooltip description={group?.description} />
      </DialogTitle>
      <DialogContent sx={{ pt: canEditCaseInfo ? "16px!important" : "" }}>
        {canEditCaseInfo && (
          <CaseInfoForm
            editable={true}
            caseInfoHandle={caseInfoHandle}
            assigneeHandle={caseAssigneeHandle}
            validHandle={caseInfoValidHandle}
            assigneeOptions={assignees}
            editableId={false}
          />
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {caseProperties.filter(isNotUndefined).map((item, index) => (
            <div className="flex items-center gap-2 [&>*:first-child]:flex-1" key={item.key}>
              <CustomPropertyCell
                key={index}
                customProperty={item}
                value={casePropertyValues[item.key]}
                setValue={(v) =>
                  setCasePropertyValues((prev) =>
                    produce(prev, (draft) => {
                      draft[item.key] = v;
                    })
                  )
                }
              />
              <DescriptionTooltip
                description={item.description}
                sx={{
                  mt: item.dataType === CustomPropertyType.Date ? "-2px" : 0,
                }}
              />
            </div>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          disabled={!caseInfoValidHandle[0]}
          onClick={saveFormChanges}
          type="submit"
          variant="contained"
          color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
